import { useCallback, useEffect, useState } from "react";
import { Players, Tables, Waitlist, Dashboard } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { toast } from "sonner";
import NewTableDialog from "@/pages/reception/NewTableDialog";
import QuickPlayerRegistration from "@/pages/reception/QuickPlayerRegistration";
import WaitlistManager from "@/pages/reception/WaitlistManager";

const Metric = ({ label, value, testId, tone = "" }) => (
    <div data-testid={testId} className="card-lux rounded-lg p-6">
        <div className="label-mini mb-2">{label}</div>
        <div className={`metric-num text-4xl ${tone}`}>{value}</div>
    </div>
);

const ReceptionKPIs = ({ stats }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric testId="kpi-tables" label="Active tables" value={stats?.active_tables ?? "—"} />
        <Metric testId="kpi-players" label="Registered players" value={stats?.total_players ?? "—"} />
        <Metric testId="kpi-buyins" label="Total buy-in" value={stats ? fmtBRL(stats.total_buyins) : "—"} tone="text-gold" />
        <Metric testId="kpi-rake" label="Accumulated rake" value={stats ? fmtBRL(stats.total_rake) : "—"} tone="text-gold" />
    </div>
);

export default function Reception() {
    const [players, setPlayers] = useState([]);
    const [tables, setTables] = useState([]);
    const [stats, setStats] = useState(null);
    const [waitlistMap, setWaitlistMap] = useState({});

    const load = useCallback(async () => {
        const [pl, tl, sm] = await Promise.all([
            Players.list(),
            Tables.list("active"),
            Dashboard.summary(),
        ]);
        setPlayers(pl);
        setTables(tl);
        setStats(sm);
        const wl = {};
        await Promise.all(tl.map(async (t) => { wl[t.id] = await Tables.getWaitlist(t.id); }));
        setWaitlistMap(wl);
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, 20000);
        return () => clearInterval(timer);
    }, [load]);

    const createTable = async (payload) => { await Tables.create(payload); load(); };
    const createPlayer = async (payload) => { await Players.create(payload); load(); };
    const addToWaitlist = async (tableId, playerId) => {
        await Tables.addToWaitlist(tableId, { player_id: playerId });
        load();
    };
    const notifyEntry = async (entry) => {
        try {
            const r = await Waitlist.notify(entry.id);
            const status = r.sms?.status;
            if (status === "mock") toast.info(`Simulated SMS sent to ${entry.player_name}`);
            else if (status === "failed") toast.error(`SMS failed: ${r.sms.error}`);
            else toast.success(`SMS sent to ${entry.player_name}`);
            load();
        } catch {
            toast.error("Failed to notify");
        }
    };
    const removeEntry = async (id) => { await Waitlist.remove(id); toast.success("Removed from waitlist"); load(); };

    return (
        <div className="space-y-8" data-testid="reception-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-4xl tracking-tight">Reception Panel</h2>
                    <p className="text-muted-foreground text-sm mt-1">Check-in, player registration and waitlist</p>
                </div>
                <NewTableDialog onSubmit={createTable} />
            </div>

            <ReceptionKPIs stats={stats} />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-4">
                    <QuickPlayerRegistration recentPlayers={players} onSubmit={createPlayer} />
                </div>
                <div className="lg:col-span-8">
                    <WaitlistManager
                        tables={tables}
                        players={players}
                        waitlistMap={waitlistMap}
                        onAdd={addToWaitlist}
                        onNotify={notifyEntry}
                        onRemove={removeEntry}
                    />
                </div>
            </div>
        </div>
    );
}
